import API_BASE_URL, { getAuthToken } from './apiConfig'

/**
 * Session timing (keep IDLE_TIMEOUT_MS in step with the backend's JWT_EXPIRES_IN):
 * - The API issues 30-minute tokens. While the user is active the app swaps the token for a
 *   fresh one before it runs out; an idle user's token just expires.
 * - After IDLE_TIMEOUT_MS without activity the user is signed out (warned WARNING_MS before).
 * - The backend also caps a session at 8 hours of total length (JWT_MAX_SESSION_HOURS).
 */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000
export const WARNING_MS = 60 * 1000
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000
const ACTIVITY_KEY = 'lastActivity'

/** Expiry time (ms) of a JWT, read from its payload; null if it can't be read. */
export function getTokenExpiry(token = getAuthToken()) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload.exp ? payload.exp * 1000 : null
  } catch (_) {
    return null
  }
}

/** Last user activity (ms), shared by all tabs through localStorage. */
export function getLastActivity() {
  try {
    return Number(localStorage.getItem(ACTIVITY_KEY)) || 0
  } catch (_) {
    return 0
  }
}

export function recordActivity(at = Date.now()) {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(at))
  } catch (_) {
    // Storage unavailable: the timeout then relies on the token expiry alone
  }
}

export function clearActivity() {
  try {
    localStorage.removeItem(ACTIVITY_KEY)
  } catch (_) {
    // ignore
  }
}

/** True when a saved session has run out (token expired or idle too long). */
export function isSessionStale(now = Date.now()) {
  const expiry = getTokenExpiry()
  const lastActivity = getLastActivity()
  return (expiry !== null && expiry <= now) || (lastActivity > 0 && now - lastActivity >= IDLE_TIMEOUT_MS)
}

/** Should the token be refreshed now? (close to expiry, and the user was recently active) */
export function shouldRefresh(now = Date.now()) {
  const expiry = getTokenExpiry()
  if (!expiry) return false
  return expiry - now <= REFRESH_BEFORE_EXPIRY_MS && now - getLastActivity() < IDLE_TIMEOUT_MS - WARNING_MS
}

let refreshing = null

/**
 * Exchange the current token for a fresh one. Returns the new token, or null if the
 * session can no longer be refreshed (the fetch wrapper then signs the user out).
 */
export function refreshSession() {
  if (refreshing) return refreshing
  refreshing = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/refresh`, { method: 'POST' })
      if (!response.ok) return null
      const data = await response.json()
      // Signed out (e.g. in another tab) while the request was in flight: don't bring the session back
      if (!data.token || !getAuthToken()) return null
      localStorage.setItem('token', data.token)
      return data.token
    } catch (_) {
      return null // network hiccup: try again on the next check
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}
